import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { findTour, stepsForRole, TourRole, TourStep } from './tours';
import { tourTargets } from './targets';
import { TourContext } from './tourContext';
import TourOverlay from './TourOverlay';
import TourButton from './TourButton';
import TourCelebration from './TourCelebration';
import './Tour.css';

const STILL_LOADING: TourStep = {
  title: 'Nothing to show yet',
  body: 'This page is still loading. Try the tour again in a moment.',
};

/** Steps whose element is on screen right now. Elements for other tabs, roles or states are skipped. */
export function visibleSteps(steps: TourStep[], name: string): TourStep[] {
  const shown = steps.filter((step) => !step.target || tourTargets(step.target).length > 0);
  return shown.length ? shown : [{ ...STILL_LOADING, title: name }];
}

interface Run { steps: TourStep[]; index: number }

const EXPLORED_KEY = (id: string) => `ikompete.tour.explored.${id}`;

/** Storage can throw (private windows, blocked site data); then nothing counts as explored. */
function wasExplored(id: string): boolean {
  try { return window.localStorage.getItem(EXPLORED_KEY(id)) === '1'; } catch { return false; }
}

function markExplored(id: string) {
  try { window.localStorage.setItem(EXPLORED_KEY(id), '1'); } catch { /* nothing to remember it in */ }
}

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname, search } = useLocation();
  const tour = useMemo(() => findTour(pathname), [pathname]);
  const [pageRole, setPageRole] = useState<TourRole | null>(null);
  const [run, setRun] = useState<Run | null>(null);
  const [inlineButtons, setInlineButtons] = useState(0);
  const [celebrating, setCelebrating] = useState<string | null>(null);
  const [explored, setExplored] = useState(false);
  useEffect(() => { setExplored(tour ? wasExplored(tour.id) : false); }, [tour]);
  const returnFocus = useRef<HTMLElement | null>(null);

  const stop = useCallback(() => {
    setRun(null);
    const el = returnFocus.current;
    returnFocus.current = null;
    if (el && el.isConnected) el.focus({ preventScroll: true });
  }, []);

  const start = useCallback(() => {
    if (!tour) return;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setRun({ steps: visibleSteps(stepsForRole(tour, pageRole), tour.name), index: 0 });
  }, [tour, pageRole]);

  // Finishing the last step: remember the page as explored and celebrate briefly.
  const finish = useCallback(() => {
    if (tour) {
      markExplored(tour.id);
      setExplored(true);
      setCelebrating(tour.name);
    }
    stop();
  }, [tour, stop]);

  const endCelebration = useCallback(() => setCelebrating(null), []);

  const registerInlineButton = useCallback(() => {
    setInlineButtons((n) => n + 1);
    return () => setInlineButtons((n) => n - 1);
  }, []);

  // A new page (or tab) means a different set of elements: end any running tour.
  useEffect(() => { setRun(null); setCelebrating(null); }, [pathname, search]);

  const value = useMemo(() => ({ tour, explored, setPageRole, active: !!run, start, registerInlineButton }),
    [tour, explored, run, start, registerInlineButton]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {/* Every page gets Explore: in the header or toolbar when it has one, otherwise floating. */}
      {tour && inlineButtons === 0 && <TourButton variant="floating" />}
      {run && (
        <TourOverlay
          steps={run.steps}
          index={run.index}
          onBack={() => setRun((r) => (r && r.index > 0 ? { ...r, index: r.index - 1 } : r))}
          onNext={() => setRun((r) => (r && r.index < r.steps.length - 1 ? { ...r, index: r.index + 1 } : r))}
          onClose={stop}
          onGo={(i) => setRun((r) => (r ? { ...r, index: Math.max(0, Math.min(i, r.steps.length - 1)) } : r))}
          onFinish={finish}
        />
      )}
      {celebrating && <TourCelebration pageName={celebrating} onClose={endCelebration} />}
    </TourContext.Provider>
  );
};

export default TourProvider;
